import React, { useMemo, useState } from 'react'
import { Button, TextField, TextInput } from '../../../../views/components'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import styles from './AddAgencyPanel.module.scss'

export type AgencyOption = {
  agencyId: string
  agencyName: string
  tradingAs?: string | null
}

type TProps = {
  /** Agencies with no settings row yet — the ones worth adding. */
  options: AgencyOption[]
  onCancel: () => void
  onPick: (agency: AgencyOption) => void
}

const MAX_RESULTS = 40

const AddAgencyPanel = ({ options, onCancel, onPick }: TProps) => {
  const [query, setQuery] = useState('')

  const results = useMemo(() => {
    const term = query.trim().toLowerCase()
    const matched = term
      ? options.filter(
          o =>
            (o.agencyName ?? '').toLowerCase().includes(term) ||
            (o.tradingAs ?? '').toLowerCase().includes(term) ||
            o.agencyId.includes(term),
        )
      : options
    return matched.slice(0, MAX_RESULTS)
  }, [options, query])

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <h2 className={styles.title}>Add an agency</h2>
        <p className={styles.intro}>Only agencies without a discount are listed.</p>
      </header>

      <div className={styles.search}>
        <TextField
          inputComponent={
            <TextInput
              type={TextFieldTypes.text}
              name="agencySearch"
              autoComplete="off"
              placeholder="Search agencies"
              value={query}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
            />
          }
        />
      </div>

      <div className={styles.results}>
        {results.length === 0 ? (
          <p className={styles.empty}>
            {options.length === 0 ? 'Every agency already has a discount.' : 'No agency matches that.'}
          </p>
        ) : (
          <ul className={styles.list}>
            {results.map(option => (
              <li key={option.agencyId}>
                <button type="button" className={styles.option} onClick={() => onPick(option)}>
                  <span className={styles['option-name']}>{option.agencyName || option.agencyId}</span>
                  {option.tradingAs && <span className={styles['option-trading']}>t/a {option.tradingAs}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
        {options.length > results.length && query.trim() === '' && (
          <p className={styles.empty}>Showing {MAX_RESULTS}. Search to narrow it down.</p>
        )}
      </div>

      <footer className={styles.footer}>
        <Button type="button" secondary onClick={onCancel}>
          Cancel
        </Button>
      </footer>
    </div>
  )
}

export default AddAgencyPanel
